/**
 * Queries and mutations for quotes and sales orders. Phase 3a · U5.
 *
 * ===========================================================================
 * THE DEBOUNCED PREVIEW IS THE UNIT'S LOAD-BEARING HOOK
 * ===========================================================================
 * The builder must never compute money. What it does instead is: hold the line
 * set as ordinary form state, wait for the operator to stop typing, POST the
 * whole basket to `/countries/:cc/quotes/preview`, and render the response.
 *
 * `useQuotePreview` is that loop, and three of its properties matter more than
 * they look:
 *
 *  1. THE QUERY KEY IS THE REQUEST BODY. React Query caches per key, so an
 *     identical basket costs nothing and a changed one is a different entry.
 *     A key of, say, the line COUNT would serve the old totals for a new price.
 *  2. `placeholderData: keepPreviousData`. While the next preview is in flight
 *     the previous SERVER numbers stay on screen, marked stale. The alternative —
 *     blanking the totals on every keystroke — makes the panel flicker, and the
 *     other alternative, interpolating client-side, is the defect.
 *  3. `retry: false`. A 501 (OQ-1), a 422 (no country price, mixed currency, a
 *     value past DECIMAL(14,2)) and a 404 (party in another country) are all
 *     ANSWERS. Retrying them three times would delay the honest message and
 *     hammer the pricing path for no reason.
 *
 * ===========================================================================
 * THERE IS NO STAGE-COUNTS ROUTE FOR QUOTES, AND THIS DOES NOT INVENT ONE
 * ===========================================================================
 * Inquiries have `GET /countries/:cc/inquiries/stage-counts`. Quotes have no
 * equivalent — spec §10 lists five quote routes and none of them counts. So the
 * tab counts come from FOUR list calls at `perPage: 1`, read off each response's
 * `total`, in one query. That is honest about its cost (four cheap round trips,
 * one cache entry, invalidated with everything else) and it is visible here
 * rather than buried in a component. If the counts ever matter enough to be a
 * route, this hook is the one call site to repoint.
 */

import { useEffect, useState } from 'react';
import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { UseMutationResult, UseQueryResult } from '@tanstack/react-query';
import type { QuoteStatus, SalesOrderStatus } from '../../domain/quotes';
import { QUOTE_TABS } from '../../domain/quotes';
import { searchParties } from '../inquiries/api';
import type { PartyDto } from '../inquiries/api';
import {
  annulQuote,
  confirmQuote,
  createQuote,
  decideDiscount,
  fetchCountryProducts,
  fetchProductModels,
  fetchQuote,
  fetchQuoteCurrencies,
  fetchQuotes,
  fetchSalesOrder,
  fetchSalesOrders,
  fetchTaxGroups,
  markQuoteLost,
  patchQuote,
  previewQuote,
  quoteKeys,
  recoverQuote,
  renderQuotePdf,
  requestDiscount,
  salesOrderKeys,
  sendQuote,
} from './api';
import type {
  CountryProductDto,
  DiscountDecisionResultDto,
  DiscountRequestDto,
  Paged,
  QuoteConfirmationDto,
  QuoteCurrenciesDto,
  QuoteDto,
  QuoteInput,
  QuotePdfRenderedDto,
  QuotePreviewDto,
  QuoteRecordDto,
  RecoveryInput,
  SalesOrderDto,
  SalesOrderRecordDto,
  TaxGroupDto,
} from './api';

/* ────────────────────────────────────────────────────────────────── reads ── */

export interface QuoteListParams {
  status: QuoteStatus | null;
  page: number;
  perPage: number;
}

export function useQuoteList(
  countryCode: string,
  params: QuoteListParams,
  enabled = true,
): UseQueryResult<Paged<QuoteDto>> {
  const query = {
    status: params.status ?? undefined,
    page: params.page,
    perPage: params.perPage,
  };
  return useQuery({
    queryKey: quoteKeys.list(countryCode, query),
    queryFn: ({ signal }) => fetchQuotes(countryCode, query, signal),
    enabled,
    // Paging must not blank the table for a round trip — the same ruling
    // `useInquiryList` records.
    placeholderData: keepPreviousData,
  });
}

export interface QuoteTabCounts {
  byStatus: Record<QuoteStatus, number>;
  /** Every quote in the country, whatever its status — the "All" tab. */
  total: number;
}

/**
 * The four tab counts plus the total. See the file header for why this is four
 * requests rather than one.
 *
 * `expired` is counted too, even though it has no tab: it is inside `total`, so
 * omitting it would make the tabs and the "All" number disagree by a row nobody
 * could find.
 */
export function useQuoteCounts(
  countryCode: string,
  enabled = true,
): UseQueryResult<QuoteTabCounts> {
  return useQuery({
    queryKey: quoteKeys.counts(countryCode),
    queryFn: async ({ signal }): Promise<QuoteTabCounts> => {
      const probes = await Promise.all([
        fetchQuotes(countryCode, { page: 1, perPage: 1 }, signal),
        ...QUOTE_TABS.map((status) =>
          fetchQuotes(countryCode, { status, page: 1, perPage: 1 }, signal),
        ),
      ]);
      const [all, ...perTab] = probes;
      const byStatus = {
        draft: 0,
        sent: 0,
        confirmed: 0,
        lost: 0,
        expired: 0,
        annulled: 0,
      } as Record<QuoteStatus, number>;
      QUOTE_TABS.forEach((status, index) => {
        byStatus[status] = perTab[index]?.total ?? 0;
      });
      return { byStatus, total: all?.total ?? 0 };
    },
    enabled,
  });
}

export function useQuoteRecord(
  id: string | null,
  enabled = true,
): UseQueryResult<QuoteRecordDto> {
  return useQuery({
    queryKey: quoteKeys.record(id ?? ''),
    queryFn: ({ signal }) =>
      id === null ? Promise.reject(new Error('no quote id')) : fetchQuote(id, signal),
    enabled: enabled && id !== null,
  });
}

export function useSalesOrderList(
  countryCode: string,
  params: { status: SalesOrderStatus | null; page: number; perPage: number },
  enabled = true,
): UseQueryResult<Paged<SalesOrderDto>> {
  const query = {
    status: params.status ?? undefined,
    page: params.page,
    perPage: params.perPage,
  };
  return useQuery({
    queryKey: salesOrderKeys.list(countryCode, query),
    queryFn: ({ signal }) => fetchSalesOrders(countryCode, query, signal),
    enabled,
    placeholderData: keepPreviousData,
  });
}

/**
 * How many sales orders exist in the country, WHATEVER their status.
 *
 * A SECOND REQUEST, ON PURPOSE. `useSalesOrderList` carries the active `status`,
 * so its `total` counts the TAB and not the country — and a screen that says
 * "0 orders in IN" while one sits in another tab has told the operator the
 * country is empty when it is not (W2). There is no counts route for sales orders
 * any more than there is for quotes (spec §10), so this is the same `perPage: 1`
 * probe `useQuoteCounts` uses, minus the per-status ones this screen does not
 * show numbers for.
 *
 * The key is `salesOrderKeys.counts`, which the shared invalidation already
 * sweeps — a confirmed quote creates an order and this number moves with it.
 */
export function useSalesOrderCountryTotal(
  countryCode: string,
  enabled = true,
): UseQueryResult<number> {
  return useQuery({
    queryKey: salesOrderKeys.counts(countryCode),
    queryFn: async ({ signal }): Promise<number> => {
      const probe = await fetchSalesOrders(countryCode, { page: 1, perPage: 1 }, signal);
      return probe.total;
    },
    enabled,
  });
}

export function useSalesOrderRecord(
  id: string | null,
  enabled = true,
): UseQueryResult<SalesOrderRecordDto> {
  return useQuery({
    queryKey: salesOrderKeys.record(id ?? ''),
    queryFn: ({ signal }) =>
      id === null ? Promise.reject(new Error('no sales order id')) : fetchSalesOrder(id, signal),
    enabled: enabled && id !== null,
  });
}

/* ─────────────────────────────────────────────────────────────── catalogue ── */

/** One catalogue row as the picker needs it: sellable here, and how it is sold. */
/**
 * The picker's order: SHELF FIRST, then product code within it — U3.4.
 *
 * ⚠ THIS WAS `localeCompare(product_code)` ALONE AND THE GROUPED PICKER SHOWED ONE SHELF
 * TWICE. Measured, not anticipated: TP-SILVER on "Addon", TSS unfiled, TSS-1Y on "Item"
 * sorts to TP-SILVER · TSS · TSS-1Y, i.e. the headings Addon · Ungrouped · Item. `SelectField`
 * emits `<optgroup>`s on RUNS — deliberately, so it never reorders a list — so a code-ordered
 * catalogue produces a chooser with two "Addon" headings in it, and an operator scrolling
 * that concludes there are two Addons.
 *
 * Four keys, in this order and each for a reason:
 *   1. UNGROUPED LAST. "Not filed yet" is a real state, and it belongs at the bottom of a
 *      chooser rather than interleaved into the middle of somebody's filing.
 *   2. `sort_order`. The country's own arrangement — the column exists precisely because
 *      alphabetical would put "Addon" above "Tally licence".
 *   3. the shelf NAME, so two shelves sharing a `sort_order` (the default is 0, so this is
 *      the ordinary case until somebody sets one) still land in a stable order rather than
 *      whichever the server happened to return.
 *   4. product code, which is the order this list has always had within a shelf.
 *
 * EXPORTED so `catalogue-order.test.ts` can hold it to all four without rendering a screen.
 */
export function compareCatalogue(a: CountryProductDto, b: CountryProductDto): number {
  const aUngrouped = a.product_group_id === null;
  const bUngrouped = b.product_group_id === null;
  if (aUngrouped !== bUngrouped) return aUngrouped ? 1 : -1;
  if (!aUngrouped) {
    const bySort = (a.product_group_sort ?? 0) - (b.product_group_sort ?? 0);
    if (bySort !== 0) return bySort;
    const byName = (a.product_group_name ?? '').localeCompare(b.product_group_name ?? '');
    if (byName !== 0) return byName;
  }
  return a.product_code.localeCompare(b.product_code);
}

export interface CatalogueEntry extends CountryProductDto {
  /** From the GLOBAL `products` row. `null` when that row was not in the page. */
  pricing_model: string | null;
  /**
   * `true` for a `per_user_month` or `slab` product — i.e. one where "how many
   * users, for how many months" is part of what was agreed and belongs on the
   * line.
   *
   * IT IS NOT A GATE, and it used to be one. Until the Phase 3a close-out this
   * field was `priceable`, and `false` meant the pricing service would refuse the
   * line with 501 (OQ-1). That refusal is void — the operator supplies the rate,
   * so every product in the catalogue prices — and the flag now decides one thing
   * only: whether the line editor offers the users/months capture fields.
   *
   * A `null` `pricing_model` is treated as NOT a subscription rather than as
   * broken: the master list is paged, and a product missing from it is a gap in
   * this client's knowledge. The cost of being wrong is two optional fields that
   * do not appear, not a product the operator cannot quote.
   */
  subscription: boolean;
}

/**
 * The country catalogue IN ONE CURRENCY'S LIST, joined to the global `pricing_model`.
 *
 * TWO REQUESTS, ONE CACHE ENTRY. `country_products` carries the price and the
 * per-country activation; `products` carries `pricing_model`, which is what says
 * whether users/months are worth capturing on the line.
 *
 * PER LIST since migration 66 (2026-09-22): a product is offered only if it has a row in
 * the quote's currency, and its price hint is that list's. `currency` is `null` until the
 * builder knows it — the salesperson has not chosen yet — and the query then does not run:
 * offering products before the currency is chosen would offer prices from a list the quote
 * may not be in.
 */
export function useCatalogue(
  countryCode: string,
  currency: string | null,
  enabled = true,
): UseQueryResult<CatalogueEntry[]> {
  return useQuery({
    queryKey: quoteKeys.catalogue(countryCode, currency ?? ''),
    queryFn: async ({ signal }): Promise<CatalogueEntry[]> => {
      const [countryRows, masters] = await Promise.all([
        fetchCountryProducts(countryCode, signal, currency ?? undefined),
        fetchProductModels(signal),
      ]);
      const modelById = new Map(masters.map((row) => [row.id, row.pricing_model]));
      return countryRows
        .filter((row) => row.active)
        .map((row) => {
          const model = modelById.get(row.product_id) ?? null;
          return {
            ...row,
            pricing_model: model,
            subscription: model !== null && model !== 'flat',
          };
        })
        .sort(compareCatalogue);
    },
    enabled: enabled && currency !== null,
    // The catalogue is admin-managed and changes rarely; a builder session should
    // not re-fetch it on every panel open.
    staleTime: 5 * 60_000,
  });
}

/**
 * The currencies a quote in this country may be in, home first (2026-09-22).
 *
 * The builder's first question in a two-currency country (CD: CDF or USD), and the one
 * currency a single-currency country sends without asking. Reference data like the
 * catalogue — it changes only when an administrator switches a second currency on.
 */
export function useQuoteCurrencies(
  countryCode: string,
  enabled = true,
): UseQueryResult<QuoteCurrenciesDto> {
  return useQuery({
    queryKey: quoteKeys.currencies(countryCode),
    queryFn: ({ signal }) => fetchQuoteCurrencies(countryCode, signal),
    enabled,
    staleTime: 5 * 60_000,
  });
}

/**
 * The pickable tax groups for the builder's group select (design U3).
 *
 * Same footing as `useCatalogue`: admin-managed reference data that changes rarely,
 * so a builder session should not re-fetch it on every panel open. The country
 * DEFAULT is in the list (`is_default`), which is what lets the select label its
 * empty choice honestly — "Country default — {name}".
 */
export function useTaxGroups(
  countryCode: string,
  enabled = true,
): UseQueryResult<TaxGroupDto[]> {
  return useQuery({
    queryKey: quoteKeys.taxGroups(countryCode),
    queryFn: ({ signal }) => fetchTaxGroups(countryCode, signal),
    enabled,
    staleTime: 5 * 60_000,
  });
}

export function usePartyLookup(
  countryCode: string,
  term: string,
  enabled: boolean,
  /** AM-1's picker toggle (v24 A1.4). Default OFF — the ruled picker posture. */
  includeHistory = false,
): UseQueryResult<Paged<PartyDto>> {
  const trimmed = term.trim();
  return useQuery({
    // the toggle is part of the KEY: 'history on' and 'history off' are two
    // different answers, and a cache that conflates them shows the wrong one.
    queryKey: quoteKeys.partySearch(countryCode, trimmed, includeHistory),
    queryFn: ({ signal }) => searchParties(countryCode, trimmed, includeHistory, signal),
    enabled: enabled && trimmed.length >= 2,
    placeholderData: keepPreviousData,
  });
}

/* ────────────────────────────────────────────────────────── THE PREVIEW ── */

/**
 * How long the builder waits after the last keystroke before asking the server
 * what the basket costs.
 *
 * 350ms. Short enough that a number appears while the operator is still looking
 * at the field they changed; long enough that typing "12000" is one request and
 * not five. It is a DEBOUNCE, not an animation, so the 150ms motion ceiling does
 * not apply to it — nothing moves.
 */
export const PREVIEW_DEBOUNCE_MS = 350;

/**
 * Debounce any value. The state write happens inside the timer callback, never
 * synchronously in the effect body — which is both correct and what keeps
 * `react-hooks/set-state-in-effect` satisfied.
 *
 * Keyed on the SERIALISED value rather than the object identity: the builder
 * rebuilds its request body on every render, so an identity dependency would
 * restart the timer forever and no preview would ever fire.
 */
export function useDebounced<T>(value: T, delayMs: number): T {
  const serialised = JSON.stringify(value ?? null);
  const [settled, setSettled] = useState<T>(value);

  useEffect(() => {
    const timer = window.setTimeout(() => {
      setSettled(JSON.parse(serialised) as T);
    }, delayMs);
    return () => {
      window.clearTimeout(timer);
    };
  }, [serialised, delayMs]);

  return settled;
}

/**
 * NEITHER FLAG ALONE MEANS "THE FIGURES ARE BEHIND". A CALLER MUST READ BOTH.
 *
 * `isStale` is the DEBOUNCE — the form has moved and the request has not been
 * sent yet. `isFetching` is the ROUND TRIP — the request is out and no answer has
 * come back. The screen is showing an older basket's money for the union of the
 * two, and a caller that renders only `isStale` presents a stale figure as
 * current for the entire network leg. That shipped once (W1) and cost the builder
 * its central claim; `QuoteBuilderPage` now derives one flag from both and
 * everything reads that.
 */
export interface PreviewState {
  /** The server's numbers, or `undefined` before the first answer. */
  data: QuotePreviewDto | undefined;
  /** True while a request is in flight — including one that will replace `data`. */
  isFetching: boolean;
  /** True when the FORM has moved on and the debounce has not yet fired. */
  isStale: boolean;
  error: unknown;
  isError: boolean;
}

/**
 * Price a basket, server-side, debounced.
 *
 * `input === null` means "there is nothing to price yet" (no party, or no lines)
 * and the hook does not fire. That is not the same as a preview that FAILED, and
 * the caller must render the two differently — an empty builder is not an error.
 */
export function useQuotePreview(
  countryCode: string,
  input: QuoteInput | null,
  enabled = true,
): PreviewState {
  const debounced = useDebounced(input, PREVIEW_DEBOUNCE_MS);
  const ready = enabled && debounced !== null && debounced.lines.length > 0;

  const query = useQuery({
    queryKey: quoteKeys.preview(countryCode, debounced),
    /*
     * The return type is ANNOTATED, not inferred. Without it the `null` branch
     * widens the function to `Promise<QuotePreviewDto> | Promise<never>`, and
     * `keepPreviousData` — which is a FUNCTION, not a value — then loses the
     * overload it needs and TypeScript infers the data type as that function.
     * The compiler error is a wall of overload text that says nothing about the
     * real cause, so it is pinned here rather than rediscovered.
     */
    queryFn: ({ signal }): Promise<QuotePreviewDto> =>
      debounced === null
        ? Promise.reject(new Error('nothing to price'))
        : previewQuote(countryCode, debounced, signal),
    enabled: ready,
    placeholderData: keepPreviousData,
    retry: false,
    // A preview is a statement about a basket at a moment. Re-firing it because a
    // window regained focus would replace numbers the operator is reading with
    // identical ones, and — if the catalogue moved underneath — with different
    // ones they did not ask for.
    refetchOnWindowFocus: false,
    // `gcTime` default is fine; the key set is bounded by how many baskets one
    // session builds.
  });

  return {
    data: query.data,
    isFetching: query.isFetching,
    // The debounce means the on-screen basket can be ahead of the priced one.
    // Saying so is the difference between "these are your numbers" and "these
    // WERE your numbers a moment ago" — and the second is the true one here.
    //
    // This covers the DEBOUNCE ONLY, and deliberately so: it is a comparison of
    // two client values and knows nothing about the request. The other half of
    // the window is `isFetching`, and the interface doc above says why a caller
    // must never read one without the other.
    isStale: JSON.stringify(input ?? null) !== JSON.stringify(debounced ?? null),
    error: query.error,
    isError: query.isError,
  };
}

/* ────────────────────────────────────────────────────────────── mutations ── */

/**
 * Everything that moves a quote invalidates BOTH the record and the country's
 * lists/counts, because every one of them changes a status, a total, or both.
 *
 * The invalidation is AWAITED inside `onSuccess` so `mutateAsync` resolves only
 * after the refetch has landed — the caller can then navigate or close a dialog
 * knowing the screen behind it is already correct, rather than showing the old
 * status for one frame.
 */
function useQuoteInvalidation(
  countryCode: string,
): (quoteId?: string) => Promise<void> {
  const client = useQueryClient();
  return async (quoteId?: string): Promise<void> => {
    await Promise.all([
      client.invalidateQueries({ queryKey: ['quotes', countryCode] }),
      client.invalidateQueries({ queryKey: ['sales-orders', countryCode] }),
      quoteId === undefined
        ? Promise.resolve()
        : client.invalidateQueries({ queryKey: quoteKeys.record(quoteId) }),
    ]);
  };
}

/**
 * Save the builder's document. A body carrying the ORIGINAL (AGT U7) goes to the recover route
 * instead — same pricing, but numbered, sent and signed at once — so the builder keeps one mutation.
 */
export function useCreateQuote(
  countryCode: string,
): UseMutationResult<QuoteRecordDto, Error, QuoteInput | (QuoteInput & RecoveryInput)> {
  const settle = useQuoteInvalidation(countryCode);
  return useMutation({
    mutationFn: (body: QuoteInput | (QuoteInput & RecoveryInput)) =>
      'recovery_kind' in body ? recoverQuote(countryCode, body) : createQuote(countryCode, body),
    onSuccess: (record) => settle(record.id),
  });
}

export function usePatchQuote(
  countryCode: string,
  quoteId: string,
): UseMutationResult<
  QuoteRecordDto,
  Error,
  { legacy_ref?: string | null; valid_till?: string | null; lines?: QuoteInput['lines'] }
> {
  const settle = useQuoteInvalidation(countryCode);
  return useMutation({
    mutationFn: (body: {
      legacy_ref?: string | null;
      valid_till?: string | null;
      lines?: QuoteInput['lines'];
    }) => patchQuote(quoteId, body),
    onSuccess: () => settle(quoteId),
  });
}

/** The variable is `confirmClock` — true only after the operator confirmed the clock (33 c)). */
export function useSendQuote(
  countryCode: string,
  quoteId: string,
): UseMutationResult<QuoteRecordDto, Error, boolean> {
  const settle = useQuoteInvalidation(countryCode);
  return useMutation({
    mutationFn: (confirmClock: boolean) =>
      sendQuote(quoteId, confirmClock ? { confirm_clock: true } : undefined),
    onSuccess: () => settle(quoteId),
  });
}

/** The variable is `confirmClock`, as {@link useSendQuote}'s. */
export function useConfirmQuote(
  countryCode: string,
  quoteId: string,
): UseMutationResult<QuoteConfirmationDto, Error, boolean> {
  const settle = useQuoteInvalidation(countryCode);
  return useMutation({
    mutationFn: (confirmClock: boolean) =>
      confirmQuote(quoteId, confirmClock ? { confirm_clock: true } : undefined),
    onSuccess: () => settle(quoteId),
  });
}

/**
 * `POST /quotes/:id/confirm` for a quote whose id is not known when the hook runs —
 * the "New sales order" flow, which CREATES the quote and then confirms it in the same
 * interaction (`QuoteBuilderPage`, mode `order`).
 *
 * A SECOND HOOK RATHER THAN A CHANGE TO {@link useConfirmQuote}, deliberately. That one
 * takes its id at construction because the record page has one; making the id a mutation
 * VARIABLE there would mean every call site could pass a different quote from the one the
 * screen is showing. Here the id genuinely arrives at call time, so it is an argument —
 * and the invalidation still names it, so the list, the counts and the new record all
 * settle before `mutateAsync` resolves.
 */
export function useConfirmQuoteById(
  countryCode: string,
): UseMutationResult<QuoteConfirmationDto, Error, { quoteId: string; confirmClock: boolean }> {
  const settle = useQuoteInvalidation(countryCode);
  return useMutation({
    mutationFn: ({ quoteId, confirmClock }: { quoteId: string; confirmClock: boolean }) =>
      confirmQuote(quoteId, confirmClock ? { confirm_clock: true } : undefined),
    onSuccess: (_result, { quoteId }) => settle(quoteId),
  });
}

export function useMarkQuoteLost(
  countryCode: string,
  quoteId: string,
): UseMutationResult<QuoteRecordDto, Error, string> {
  const settle = useQuoteInvalidation(countryCode);
  return useMutation({
    mutationFn: (reason: string) => markQuoteLost(quoteId, reason),
    onSuccess: () => settle(quoteId),
  });
}

/** AGT design §3 U2, D7 — `sent → annulled`, the Lost hook's shape. */
export function useAnnulQuote(
  countryCode: string,
  quoteId: string,
): UseMutationResult<QuoteRecordDto, Error, string> {
  const settle = useQuoteInvalidation(countryCode);
  return useMutation({
    mutationFn: (reason: string) => annulQuote(quoteId, reason),
    onSuccess: () => settle(quoteId),
  });
}

export function useRequestDiscount(
  countryCode: string,
  quoteId: string,
): UseMutationResult<DiscountRequestDto, Error, { pct: string; reason: string }> {
  const settle = useQuoteInvalidation(countryCode);
  return useMutation({
    mutationFn: (body: { pct: string; reason: string }) => requestDiscount(quoteId, body),
    // The quote itself is NOT changed by a request — no money moves until somebody
    // decides. The invalidation is still correct: `updated_at` did not move, but a
    // caller re-reading the record is cheap and a stale one is not.
    onSuccess: () => settle(quoteId),
  });
}

export function useDecideDiscount(
  countryCode: string,
  quoteId: string,
): UseMutationResult<
  DiscountDecisionResultDto,
  Error,
  { decision: 'approve' | 'reject'; decision_note?: string | null }
> {
  const settle = useQuoteInvalidation(countryCode);
  return useMutation({
    mutationFn: (body: { decision: 'approve' | 'reject'; decision_note?: string | null }) =>
      decideDiscount(quoteId, body),
    onSuccess: () => settle(quoteId),
  });
}

/**
 * `POST /quotes/:id/pdf`.
 *
 * Invalidates the record because the render writes `pdf_path` — which is what the
 * record's "Download PDF" affordance keys on. A screen that did not refetch would
 * offer the download only after a manual reload.
 */
export function useRenderQuotePdf(
  countryCode: string,
  quoteId: string,
): UseMutationResult<QuotePdfRenderedDto, Error, void> {
  const settle = useQuoteInvalidation(countryCode);
  return useMutation({
    mutationFn: () => renderQuotePdf(quoteId),
    onSuccess: () => settle(quoteId),
  });
}
